import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useGetProgram } from '@interxfi/api-client/program/get-program';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button';
import { type DataDefinition, DefinitionBox } from '@interxfi/design-system/components/definition-box/definition-box';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  type WrappingLayoutColumnDef,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Tooltip } from '@interxfi/design-system/components/tooltip/tooltip';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxmUserauthV1ProgramState } from '@interxfi/types';
import { PageTabRail } from '../../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../components/router/router-tabbed-navigation';
import {
  getProgramDetailListItemSummarySections,
  getProgramStateListItems,
} from '../../../../../../../../../features/userauth/program/program-list-items';
import { ProgramPageHeader } from '../../../../../../../../../features/userauth/program/program-page-header';
import { getProgramConfigurationPath } from '../../../../../../../../../features/userauth/program/program-routing';
import { UpdateProgramDetailsDrawer } from '../../../../../../../../../features/userauth/program/update-program-details-drawer';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';

export const Route = createFileRoute('/_app/_platform/_context-required/program/_program/$programId/_program/configuration/')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.ProgramConfiguration,
      label: 'Details',
      order: 0,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.ProgramConfiguration>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `program-${params.programId}-configuration`,
        label: 'Configuration',
        to: getProgramConfigurationPath(params.programId),
      }),
    }) satisfies AggregateBreadcrumb,
  component: ProgramConfiguration,
});

const programDetailDefinitions: DataDefinition[] = [
  {
    id: 'programInformation',
    title: [{ id: 'program', label: 'Program Information' }],
    definition: (
      <VerticalStack gap={2}>
        <span>
          <Typography weight="bold">Name</Typography> can be one of the following:
        </span>

        <span>
          The legal name or official name of the company as registered with government authorities. For broker-dealers, this is typically the name
          listed on BrokerCheck.
        </span>

        <span>The DBA or trade name the company conducts business under.</span>
      </VerticalStack>
    ),
  },
  {
    id: 'correspondentInformation',
    title: [{ id: 'correspondentInformation', label: 'Correspondent Information' }],
    definition: (
      <VerticalStack gap={2}>
        <span>
          <Typography weight="bold">Legal Name</Typography> is the legal or official name of the correspondent firm as registered with government
          authorities. For broker-dealers, this is typically the name listed on BrokerCheck.
        </span>

        <span>
          <Typography weight="bold">Web Domains</Typography> lists the correspondent's public-facing website domains, used to verify the firm's online
          presence during due diligence.
        </span>

        <span>
          <Typography weight="bold">DBA</Typography> is the trade name the correspondent conducts business under, if different from its legal name.
        </span>

        <span>
          <Typography weight="bold">Tax ID / EIN</Typography> is the correspondent's federal tax identification number, used for tax reporting
          purposes.
        </span>

        <span>
          <Typography weight="bold">CRD Number</Typography> and <Typography weight="bold">SEC Registration Number</Typography> identify the
          correspondent with FINRA and the SEC, and only appear when the registration type is Broker-Dealer or Investment Adviser.
        </span>

        <span>
          <Typography weight="bold">Main Office Address</Typography> is the correspondent's primary business address as registered with regulators.
        </span>

        <span>
          <Typography weight="bold">Mailing Address</Typography> is used for regulatory correspondence with the firm. If one is not provided, the Main
          Office Address will be used in its place.
        </span>
      </VerticalStack>
    ),
  },
  {
    id: 'customerSupportContact',
    title: [{ id: 'customerSupportContact', label: 'Customer Support Contact' }],
    definition: (
      <VerticalStack gap={2}>
        <span>
          <Typography weight="bold">Program Display Name</Typography> will appear on all customer-facing documents. If one is not provided, the
          Program <Typography weight="bold">Name</Typography> will be used in its place.
        </span>

        <span>
          <Typography weight="bold">Website, Email, and Phone</Typography> will appear on Statements and Trade Confirmations as a contact for customer
          inquiries.
        </span>

        <span>
          <Typography weight="bold">Phone (Tax Forms)</Typography> will appear on all tax forms per rigid IRS rules with the intent that it will be
          used for customer inquiries.
        </span>

        <span>
          <Typography weight="bold">Mailing Address</Typography> will appear on all customer-facing documents and may be used for customer inquiries.
          It will also serve as the return address on physical shareholder or proxy materials, including any undeliverable mail.
        </span>
      </VerticalStack>
    ),
  },
  {
    id: 'correspondentVendors',
    title: [{ id: 'correspondentVendors', label: 'Correspondent Vendors' }],
    definition: (
      <VerticalStack gap={2}>
        <span>
          <Typography weight="bold">CIP Identity</Typography> is the vendor used to verify customer identity as part of the correspondent's Customer
          Identification Program during onboarding.
        </span>

        <span>
          <Typography weight="bold">CIP OFAC</Typography> is the vendor used to screen customers against OFAC and other sanctions lists as part of the
          Customer Identification Program.
        </span>

        <span>
          <Typography weight="bold">Bank Account Validation</Typography> is the vendor used to verify ownership and details of a customer's linked
          bank account before enabling transfers.
        </span>
      </VerticalStack>
    ),
  },
];

function getListItems(data: IxmUserauthV1ProgramState | undefined) {
  const items = getProgramStateListItems(data);

  return [
    {
      id: 'main',
      items: [items.metadata.createdAt, items.metadata.updatedAt, items.programId],
    },
    ...getProgramDetailListItemSummarySections(items.data.details),
  ] as const satisfies ReadonlyListGroup[];
}

const rightColumn = { width: { type: 'fixed', width: 320 }, overflow: 'hidden' } satisfies Omit<WrappingLayoutColumnDef, 'render'>;

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load program', 'view this program');

function ProgramConfiguration() {
  const { programId } = Route.useParams();
  const request = { programId } as const;
  const { activeTenantIsPlatform } = useActiveRealmAPIContext();
  const { data, error, isPending } = useGetProgram(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.program), [data?.program]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <ProgramPageHeader programId={programId} withEntityMenu />
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <PageTabRail
          actions={
            activeTenantIsPlatform ? (
              <RequiresAccess write>
                <UpdateProgramDetailsDrawer programId={programId} trigger={<Button size="small">Edit</Button>} />
              </RequiresAccess>
            ) : (
              <Tooltip
                activator={
                  <Button disabled size="small">
                    Edit
                  </Button>
                }
                maxWidth={200}
              >
                Please contact the Interchange Team to make changes to your program's details.
              </Tooltip>
            )
          }
          group={TabbedNavigationGroup.ProgramConfiguration}
        />
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <AppReadonlyListStack groups={listItems} isPending={showPending} titleGap={4} />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...rightColumn,
            render: (isWrapping) => (isWrapping ? null : <DefinitionBox definitions={programDetailDefinitions} />),
          }}
          wrap="forward"
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
